import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en.json';
import ar from '../locales/ar.json';
import generalEn from '../locales/general.en.json';
import generalAr from '../locales/general.ar.json';
import { isGeneral, variantConfig } from '../config/appVariant';

const LanguageContext = createContext();

// Recursively merge an overlay onto a base translations object, returning a new
// object. Leaf values in the overlay win; keys absent from the overlay keep the
// base value. Used to apply the per-variant string overrides (general.*.json)
// on top of the full teacher locale so feature components need no edits.
const deepMerge = (base, overlay) => {
  const out = { ...base };
  for (const key of Object.keys(overlay)) {
    const o = overlay[key];
    if (o && typeof o === 'object' && !Array.isArray(o) &&
        out[key] && typeof out[key] === 'object' && !Array.isArray(out[key])) {
      out[key] = deepMerge(out[key], o);
    } else {
      out[key] = o;
    }
  }
  return out;
};

// For the general variant, overlay the override files; otherwise use the base.
const EN = isGeneral ? deepMerge(en, generalEn) : en;
const AR = isGeneral ? deepMerge(ar, generalAr) : ar;

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const translations = {
    en: EN,
    ar: AR
  };

  useEffect(() => {
    // Set document direction and language
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', language === 'ar' ? 'rtl' : 'ltr');
    // Keep the browser tab title in sync with the current variant + language.
    document.title = variantConfig.appName[language] || variantConfig.appName.en;
    // index.html ships the teacher wording; the general build corrects it here.
    document.querySelector('meta[name="description"]')
      ?.setAttribute('content', variantConfig.description[language] || variantConfig.description.en);
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isRTL: language === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};